import { c as _c } from "react/compiler-runtime";
import React from "react";
import { Check, Copy, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@repo/blacksmith-ui/dropdown-menu";
import { CursorLogo } from "@/components/icons/CursorLogo";
import { CodesmithIcon } from "@/components/icons/CodesmithIcon";
import { cn } from "@repo/blacksmith-ui/cn";
import { assetUrl } from "@/lib/assetUrl";
type FixAction = "fix-cursor" | "fix-codesmith" | "copy-prompt";
function CursorLogoAdaptive(t0) {
  const $ = _c(11);
  const {
    size,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("block dark:hidden", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== size || $[3] !== t1) {
    t2 = <CursorLogo size={size} className={t1} />;
    $[2] = size;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = assetUrl("CUBE_2D_DARK.svg");
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== size) {
    t4 = <img src={t3} alt="" width={size} height={size} className="hidden dark:block" />;
    $[6] = size;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <>{t2}{t4}</>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
interface FixAiFloatingButtonProps {
  /** Number of visible failed/error tests */
  visibleFailedTestsCount: number;
  /** Whether the primary action is available */
  isPrimaryDisabled?: boolean;
  /** Currently selected default action */
  selectedAction: FixAction;
  /** Handler for fixing all visible failed tests */
  onFixAllTests: () => void;
  /** Handler for fixing the selected test only */
  onFixSingleTest: () => void;
  /** Handler for fixing the selected test in Codesmith */
  onFixSingleTestInCodesmith: () => void;
  /** Handler for copying prompt for the selected test only */
  onCopySingleTest: () => void;
  /** Handler when a dropdown item is selected (changes default + executes) */
  onSelectAction: (action: FixAction) => void;
  /** Whether the copy action was recently triggered (shows Copied!) */
  copied?: boolean;
  /** Whether the org has Codesmith enabled */
  codesmithEnabled?: boolean;
  /** Additional class names for the container */
  className?: string;
}

/**
 * Floating "Fix" button that appears in the bottom-right corner of the Tests tab.
 * - Shows test count badge
 * - Main click executes the default action (Cursor or Copy)
 * - Settings dropdown lets user change the default (and immediately executes)
 */
export function FixAiFloatingButton(t0) {
  const $ = _c(59);
  const {
    visibleFailedTestsCount,
    isPrimaryDisabled: t1,
    selectedAction,
    onFixAllTests,
    onFixSingleTest,
    onFixSingleTestInCodesmith,
    onCopySingleTest,
    onSelectAction,
    copied: t2,
    codesmithEnabled: t3,
    className
  } = t0;
  const isPrimaryDisabled = t1 === undefined ? false : t1;
  const copied = t2 === undefined ? false : t2;
  const codesmithEnabled = t3 === undefined ? false : t3;
  if (visibleFailedTestsCount === 0) {
    return null;
  }
  const isCodesmithMode = selectedAction === "fix-codesmith";
  const isCursorMode = selectedAction === "fix-cursor";
  const isSplit = visibleFailedTestsCount > 1;
  const shouldDisablePrimary = isSplit ? isPrimaryDisabled : false;
  const shouldUseAllForPrimary = !isSplit && isPrimaryDisabled;
  let t4;
  if ($[0] !== isCodesmithMode || $[1] !== isCursorMode || $[2] !== onCopySingleTest || $[3] !== onFixAllTests || $[4] !== onFixSingleTest || $[5] !== onFixSingleTestInCodesmith || $[6] !== shouldDisablePrimary || $[7] !== shouldUseAllForPrimary) {
    t4 = () => {
      if (shouldUseAllForPrimary) {
        onFixAllTests();
        return;
      }
      if (shouldDisablePrimary) {
        return;
      }
      if (isCodesmithMode) {
        onFixSingleTestInCodesmith();
        return;
      }
      if (isCursorMode) {
        onFixSingleTest();
        return;
      }
      onCopySingleTest();
    };
    $[0] = isCodesmithMode;
    $[1] = isCursorMode;
    $[2] = onCopySingleTest;
    $[3] = onFixAllTests;
    $[4] = onFixSingleTest;
    $[5] = onFixSingleTestInCodesmith;
    $[6] = shouldDisablePrimary;
    $[7] = shouldUseAllForPrimary;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handlePrimaryClick = t4;
  let t5;
  if ($[9] !== className) {
    t5 = cn("fixed bottom-[34px] right-6 z-50 flex items-center", "bg-white dark:bg-[#242526] border border-black/[0.24] rounded-full shadow-[0px_11px_12px_0px_rgba(0,0,0,0.15)]", "p-2", className);
    $[9] = className;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== copied || $[12] !== handlePrimaryClick || $[13] !== isCodesmithMode || $[14] !== isCursorMode || $[15] !== isSplit || $[16] !== onFixAllTests || $[17] !== shouldDisablePrimary || $[18] !== visibleFailedTestsCount) {
    t6 = <div className="flex items-center gap-1">{isSplit ? <div className="flex items-center"><button onClick={handlePrimaryClick} disabled={shouldDisablePrimary} className={cn("h-8 border border-[#2a2c2c] rounded-l-full", "bg-gradient-to-r from-[#cceeff] to-[#dbf4ba]", "px-3 py-[2px] flex items-center justify-center", "text-[13px] font-medium leading-[22px] tracking-[-0.1px] text-[#202122] whitespace-nowrap", "transition-colors", !shouldDisablePrimary && "hover:brightness-95", shouldDisablePrimary && "opacity-60 cursor-not-allowed")}>{isCodesmithMode ? "Fix in [code]smith" : isCursorMode ? "Fix in Cursor" : copied ? "Copied!" : "Copy Fix Prompt"}</button><button onClick={onFixAllTests} className={cn("h-8 w-[87px] border-b border-r border-t border-[#2a2c2c] rounded-r-full", "bg-gradient-to-r from-[#d9f3c6] to-[#f3fc50]", "px-3 py-[2px] flex items-center justify-center", "text-[13px] font-medium leading-[22px] tracking-[-0.1px] text-[#202122] whitespace-nowrap", "transition-colors hover:brightness-95")}>{`Fix all ${visibleFailedTestsCount}`}</button></div> : <button onClick={handlePrimaryClick} disabled={shouldDisablePrimary} className={cn("h-8 border border-[#2a2c2c] rounded-full", "bg-gradient-to-r from-[#cceeff] to-[#f3fc50]", "px-3 py-[2px] flex items-center justify-center", "text-[13px] font-medium leading-[22px] tracking-[-0.1px] text-[#202122] whitespace-nowrap", "transition-colors", !shouldDisablePrimary && "hover:brightness-95", shouldDisablePrimary && "opacity-60 cursor-not-allowed")}>{isCodesmithMode ? "Fix in [code]smith" : isCursorMode ? "Fix in Cursor" : copied ? "Copied!" : "Copy Fix Prompt"}</button>}</div>;
    $[11] = copied;
    $[12] = handlePrimaryClick;
    $[13] = isCodesmithMode;
    $[14] = isCursorMode;
    $[15] = isSplit;
    $[16] = onFixAllTests;
    $[17] = shouldDisablePrimary;
    $[18] = visibleFailedTestsCount;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== isCodesmithMode || $[21] !== isCursorMode) {
    t7 = <div className="flex items-center justify-center w-[18px] h-[18px]">{isCodesmithMode ? <CodesmithIcon className="text-[#202122] dark:text-neutral-200" /> : isCursorMode ? <CursorLogoAdaptive size={18} className="text-[#202122] dark:text-neutral-200" /> : <Copy size={18} className="text-[#202122] dark:text-neutral-200" />}</div>;
    $[20] = isCodesmithMode;
    $[21] = isCursorMode;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex items-center justify-center w-4 h-4"><ChevronDown className="w-4 h-4 rotate-180 text-[#2a2c2c] dark:text-neutral-200" strokeWidth={1} /></div>;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t7) {
    t9 = <DropdownMenuTrigger asChild={true}><button className="flex items-center gap-0.5 pl-2 pr-1 py-0 cursor-pointer transition-colors outline-none focus-visible:outline-none">{t7}{t8}</button></DropdownMenuTrigger>;
    $[24] = t7;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== onSelectAction) {
    t10 = () => onSelectAction("fix-cursor");
    $[26] = onSelectAction;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  let t12;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <CursorLogoAdaptive size={14} className="text-neutral-500 dark:text-neutral-300" />;
    t12 = <span>Fix in Cursor</span>;
    $[28] = t11;
    $[29] = t12;
  } else {
    t11 = $[28];
    t12 = $[29];
  }
  let t13;
  if ($[30] !== selectedAction) {
    t13 = selectedAction === "fix-cursor" && <Check size={14} className="ml-auto text-neutral-500" />;
    $[30] = selectedAction;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t10 || $[33] !== t13) {
    t14 = <DropdownMenuItem onClick={t10} className="flex items-center gap-2">{t11}{t12}{t13}</DropdownMenuItem>;
    $[32] = t10;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== onSelectAction) {
    t15 = () => onSelectAction("copy-prompt");
    $[35] = onSelectAction;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Copy size={14} className="text-neutral-500 dark:text-neutral-300" />;
    t17 = <span>Copy prompt</span>;
    $[37] = t16;
    $[38] = t17;
  } else {
    t16 = $[37];
    t17 = $[38];
  }
  let t18;
  if ($[39] !== selectedAction) {
    t18 = selectedAction === "copy-prompt" && <Check size={14} className="ml-auto text-neutral-500" />;
    $[39] = selectedAction;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t15 || $[42] !== t18) {
    t19 = <DropdownMenuItem onClick={t15} className="flex items-center gap-2">{t16}{t17}{t18}</DropdownMenuItem>;
    $[41] = t15;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== codesmithEnabled || $[45] !== onSelectAction || $[46] !== selectedAction) {
    t20 = codesmithEnabled && <DropdownMenuItem onClick={() => onSelectAction("fix-codesmith")} className="flex items-center gap-2"><CodesmithIcon className="text-neutral-500 dark:text-neutral-300" /><span>Fix in [code]smith</span>{selectedAction === "fix-codesmith" && <Check size={14} className="ml-auto text-neutral-500" />}</DropdownMenuItem>;
    $[44] = codesmithEnabled;
    $[45] = onSelectAction;
    $[46] = selectedAction;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t14 || $[49] !== t19 || $[50] !== t20) {
    t21 = <DropdownMenuContent align="end" side="top" sideOffset={8}>{t14}{t19}{t20}</DropdownMenuContent>;
    $[48] = t14;
    $[49] = t19;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== t21 || $[53] !== t9) {
    t22 = <DropdownMenu>{t9}{t21}</DropdownMenu>;
    $[52] = t21;
    $[53] = t9;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== t22 || $[56] !== t5 || $[57] !== t6) {
    t23 = <div className={t5}>{t6}{t22}</div>;
    $[55] = t22;
    $[56] = t5;
    $[57] = t6;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  return t23;
}